// apps-kb-helpers.js - Shared utility functions

import state from './apps-state.js';
import * as render from './apps-render.js';

export function getLayout() {
    return render.calculateLayout();
}

export function getCurrentIcons() {
    if (state.openFolder) {
        return window.icons.filter(icon => icon.kfolder === state.openFolder.id);
    }
    return window.icons.filter(icon => icon.kfolder === 0);
}

export function getCurrentScreen() {
    if (state.openFolder) {
        return state.currentFolderScreen;
    }
    return state.currentScreen;
}

export function getCurrentScreenForRegions() {
    // Returns the screen index adjusted for temporary screens
    // Used when calculating region numbers based on screen position
    const visualScreen = getCurrentScreen();
    
    // If there's a left temporary screen, real screens are offset by +1 visually
    // but their regions haven't been shifted yet, so we need to subtract 1
    if (state.temporaryScreenSide === 'left' && visualScreen > 0) {
        return visualScreen - 1;
    }
    
    return visualScreen;
}

export function regionPositionToRankFile(region, position, layout, isFolder) {
    const visualPos = render.calculateVisualPosition(region, position, layout);
    
    if (isFolder) {
        // Folders are always single-screen, return as-is
        return visualPos;
    }
    
    // For main screen, convert to screen-relative coordinates
    const regionsPerScreen = layout.leaves * layout.bands;
    const screenNum = Math.floor(region / regionsPerScreen);
    const regionWithinScreen = region % regionsPerScreen;
    
    // Calculate visual position within the screen
    const regionLeaf = Math.floor(regionWithinScreen / layout.bands);
    const regionBand = regionWithinScreen % layout.bands;
    const posRank = Math.floor(position / 4);
    const posFile = position % 4;
    
    return {
        rank: regionBand * 3 + posRank,
        file: regionLeaf * 4 + posFile,
        screen: screenNum
    };
}

export function rankFileToRegionPosition(rank, file, layout, isFolder) {
    if (isFolder) {
        // Folders have a single band (row) of regions
        // Region layout: [0][1][2][3]... (horizontal)
        const regionLeaf = Math.floor(file / 4);
        const region = regionLeaf;  // Simple: region equals column index
        
        const posRank = rank % 3;  // Rank within the region (0-2)
        const posFile = file % 4;  // File within the region (0-3)
        const position = posRank * 4 + posFile;
        
        return { region, position };
    }
    
    return render.visualPositionToRegionPosition(rank, file, layout);
}

export function getIconAtRankFile(rank, file, icons, layout, isFolder) {
    // Temporary screens are always empty
    if (state.temporaryScreenSide !== null) return null;
    
    const currentScreen = getCurrentScreenForRegions();
    const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
    const screenStartRegion = currentScreen * regionsPerScreen;
    const slotInfo = rankFileToRegionPosition(rank, file, layout, isFolder);
    
    if (!slotInfo) {
        return null;
    }
    
    const actualRegion = screenStartRegion + slotInfo.region;
    return icons.find(icon => 
        icon.region === actualRegion && 
        icon.position === slotInfo.position
    );
}

export function isRankOccupied(rank, icons, layout, isFolder) {
    // Temporary screens are always empty
    if (state.temporaryScreenSide !== null) return false;
    
    const totalFiles = layout.leaves * 4;
    const currentScreen = getCurrentScreenForRegions();
    const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
    const screenStartRegion = currentScreen * regionsPerScreen;
    
    for (let file = 0; file < totalFiles; file++) {
        const slotInfo = rankFileToRegionPosition(rank, file, layout, isFolder);
        if (!slotInfo) continue;
        
        const actualRegion = screenStartRegion + slotInfo.region;
        if (icons.some(icon => icon.region === actualRegion && icon.position === slotInfo.position)) {
            return true;
        }
    }
    return false;
}

export function isFileOccupied(file, icons, layout, isFolder) {
    // Temporary screens are always empty
    if (state.temporaryScreenSide !== null) return false;
    
    const currentScreen = getCurrentScreenForRegions();
    const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
    const ranksPerScreen = isFolder ? 3 : layout.bands * 3;
    const screenStartRegion = currentScreen * regionsPerScreen;
    
    for (let rank = 0; rank < ranksPerScreen; rank++) {
        const slotInfo = rankFileToRegionPosition(rank, file, layout, isFolder);
        if (!slotInfo) continue;
        
        const actualRegion = screenStartRegion + slotInfo.region;
        if (icons.some(icon => icon.region === actualRegion && icon.position === slotInfo.position)) {
            return true;
        }
    }
    return false;
}

export function getIconsInRank(rank, icons, layout, isFolder) {
    // Temporary screens are always empty
    if (state.temporaryScreenSide !== null) return [];
    
    const currentScreen = getCurrentScreenForRegions();
    const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
    const screenStartRegion = currentScreen * regionsPerScreen;
    const screenEndRegion = screenStartRegion + regionsPerScreen - 1;
    const rankIcons = [];
    const totalFiles = layout.leaves * 4;
    
    for (let file = 0; file < totalFiles; file++) {
        const slotInfo = rankFileToRegionPosition(rank, file, layout, isFolder);
        if (!slotInfo) continue;
        
        const actualRegion = screenStartRegion + slotInfo.region;
        if (actualRegion < screenStartRegion || actualRegion > screenEndRegion) {
            continue;
        }
        
        const icon = icons.find(icon => 
            icon.region === actualRegion && 
            icon.position === slotInfo.position
        );
        
        if (icon) {
            rankIcons.push({ icon, file, region: actualRegion, position: slotInfo.position });
        }
    }
    
    return rankIcons.sort((a, b) => a.file - b.file);
}

export function getMaxOccupiedFile(icons, layout, isFolder) {
    const totalFiles = layout.leaves * 4;
    let maxFile = -1;
    
    for (let file = 0; file < totalFiles; file++) {
        if (isFileOccupied(file, icons, layout, isFolder)) {
            maxFile = file;
        }
    }
    
    return maxFile;
}

export function getMinOccupiedRank(icons, layout, isFolder) {
    const totalRanks = isFolder ? 3 : layout.bands * 3;
    
    for (let rank = 0; rank < totalRanks; rank++) {
        if (isRankOccupied(rank, icons, layout, isFolder)) {
            return rank;
        }
    }
    
    return -1;
}

export function getMaxOccupiedRank(icons, layout, isFolder) {
    const totalRanks = isFolder ? 3 : layout.bands * 3;
    let maxRank = -1;
    
    for (let rank = 0; rank < totalRanks; rank++) {
        if (isRankOccupied(rank, icons, layout, isFolder)) {
            maxRank = rank;
        }
    }
    
    return maxRank;
}

export function getMinOccupiedFile(icons, layout, isFolder) {
    const totalFiles = layout.leaves * 4;
    
    for (let file = 0; file < totalFiles; file++) {
        if (isFileOccupied(file, icons, layout, isFolder)) {
            return file;
        }
    }
    
    return -1;
}

export function getLeftmostIconInRank(rank, icons, layout, isFolder) {
    const totalFiles = layout.leaves * 4;
    
    for (let file = 0; file < totalFiles; file++) {
        const icon = getIconAtRankFile(rank, file, icons, layout, isFolder);
        if (icon) {
            return { icon, rank, file };
        }
    }
    return null;
}

export function getRightmostIconInRank(rank, icons, layout, isFolder) {
    const totalFiles = layout.leaves * 4;
    
    for (let file = totalFiles - 1; file >= 0; file--) {
        const icon = getIconAtRankFile(rank, file, icons, layout, isFolder);
        if (icon) {
            return { icon, rank, file };
        }
    }
    return null;
}

export function getTopmostIconInFile(file, icons, layout, isFolder) {
    const totalRanks = isFolder ? 3 : layout.bands * 3;
    
    for (let rank = 0; rank < totalRanks; rank++) {
        const icon = getIconAtRankFile(rank, file, icons, layout, isFolder);
        if (icon) {
            return { icon, rank, file };
        }
    }
    return null;
}

export function getComputedDimensions() {
    const iconSize = parseInt(
        getComputedStyle(document.documentElement)
            .getPropertyValue('--icon-size')
    ) || state.config.iconSize;
    
    const gapH = parseInt(
        getComputedStyle(document.documentElement)
            .getPropertyValue('--icon-gap-horizontal')
    ) || state.config.gapHorizontal;
    
    const gapV = parseInt(
        getComputedStyle(document.documentElement)
            .getPropertyValue('--icon-gap-vertical')
    ) || state.config.gapVertical;
    
    return { iconSize, gapH, gapV };
}

export function getMaxOccupiedGrandRankInFolder(icons) {
    // Grand rank: a rank across the entirety of a folder (all pages)
    // This differs from regular rank which is screen-relative
    let maxGrandRank = -1;
    icons.forEach(icon => {
        const grandRank = Math.floor(icon.position / 4);
        if (grandRank > maxGrandRank) maxGrandRank = grandRank;
    });
    return maxGrandRank;
}

export function getAccessibleRanksInFolder(inMoveMode) {
    if (!state.openFolder) return 3;
    
    const folderIcons = window.icons.filter(icon => icon.kfolder === state.openFolder.id);
    const maxOccupiedGrandRank = getMaxOccupiedGrandRankInFolder(folderIcons);
    
    if (inMoveMode) {
        // In move mode, allow one additional rank beyond occupied (but max 2, which is rank index for the 3rd rank)
        return Math.min(maxOccupiedGrandRank + 2, 3);
    } else {
        // In normal mode, only show occupied ranks
        return maxOccupiedGrandRank + 1;
    }
}

export function isCurrentScreenEmptyWithDeleteButton() {
    const isFolder = !!state.openFolder;
    const currentScreen = isFolder ? state.currentFolderScreen : state.currentScreen;
    
    // Check if screen has a delete button (it's rendered when empty and not the last screen)
    const deleteButtons = document.querySelectorAll('.delete-screen-icon-button');
    
    // Find the button for the current screen
    for (const button of deleteButtons) {
        const buttonScreenIndex = parseInt(button.dataset.screenIndex);
        const buttonIsFolder = button.dataset.isFolder === 'true';
        
        if (buttonScreenIndex === currentScreen && buttonIsFolder === isFolder) {
            return true;
        }
    }
    
    return false;
}

export function getDeleteButtonElement() {
    const isFolder = !!state.openFolder;
    const currentScreen = isFolder ? state.currentFolderScreen : state.currentScreen;
    
    // Find the button for the current screen and context (folder vs main)
    const deleteButtons = document.querySelectorAll('.delete-screen-icon-button');
    
    for (const button of deleteButtons) {
        const buttonScreenIndex = parseInt(button.dataset.screenIndex);
        const buttonIsFolder = button.dataset.isFolder === 'true';
        
        if (buttonScreenIndex === currentScreen && buttonIsFolder === isFolder) {
            return button;
        }
    }
    
    return null;
}
